import { createRouter, createWebHistory } from 'vue-router';

import { toBase32 } from '@/helpers/toBase32Helper.js';
import store from '@/store';
import { saveScrollPosition, restoreScrollPosition } from '@/helpers/scrollControlHelper';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      meta: { layout: 'default', auth: true },
      beforeEnter: (to, from, next) => {
        const isAuthenticated = store.getters.getAuthStatus;
        if (!isAuthenticated) {
          // OLD: redirected to the 'welcome' route, which is commented out in
          // this router — vue-router throws "No match for {name: 'welcome'}"
          // and ABORTS the navigation, so clicking "Community" in the leftbar
          // silently did nothing once the session had expired.
          // OLD code:
          // next({ name: 'welcome', replace: true });
          next({ path: '/sign-in', replace: true });
          return;
        }
        next();
      },
      component: () => import('../views/index.vue'),
    },
    // {
    //   path: '/welcome',
    //   name: 'welcome',
    //   meta: { layout: 'guest', auth: false },
    //   beforeEnter: (to, from, next) => {
    //     const isAuthenticated = store.getters.getAuthStatus;
    //     if (isAuthenticated) {
    //       next({ name: 'home', replace: true });
    //       return;
    //     }
    //     next();
    //   },
    //   component: () => import('../views/LandingPage.vue'),
    // },
    {
      path: '/search',
      name: 'search',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/search.vue'),
    },
    // {
    //   path: '/modern-feed',
    //   name: 'modern-feed',
    //   meta: { layout: 'default', auth: true },
    //   component: () => import('../views/ModernFeedView.vue'),
    // },
    // {
    //   path: '/sign-in-with-x',
    //   name: 'sign-in-with-x',
    //   meta: { layout: 'guest', auth: false },
    //   component: () => import('../views/sign-in-with-x.vue'),
    // },
    // {
    //   path: '/oauth-callback-bluesky',
    //   name: 'oauth-callback-bluesky',
    //   meta: { layout: 'guest', auth: false },
    //   component: () => import('../views/oauth-callback-bluesky.vue'),
    // },
    // {
    //   path: '/declaration',
    //   name: 'declarationPage',
    //   meta: { layout: 'guest', auth: false },
    //   component: () => import('../views/declarationPage.vue'),
    // },
    {
      path: '/about',
      name: 'aboutPage',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/about/aboutus.vue'),
    },
    {
      path: '/privacy',
      name: 'privacy',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/about/privacy.vue'),
    },
    {
      path: '/term-of-service',
      name: 'terms',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/about/terms.vue'),
    },
    {
      path: '/child-safety',
      name: 'childSafety',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/about/childsafety.vue'),
    },
    {
      path: '/post/:id',
      name: 'post',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/post.vue'),
    },
    {
      path: '/reply/:id',
      name: 'reply',
      meta: { layout: 'default', auth: true, isReply: true },
      component: () => import('../views/post.vue'),
    },
    {
      path: '/sign-in',
      name: 'sign-in',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/login.vue'),
    },
    {
      path: '/profile/:id',
      name: 'profile',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/profile-view.vue'),
    },
    {
      path: '/user/:username',
      name: 'username',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/profile-view.vue'),
    },
    {
      path: '/group/:id',
      name: 'org',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/org.vue'),
    },
    {
      path: '/groups',
      name: 'groups',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/groups.vue'),
    },
    {
      path: '/groups/:userId',
      name: 'user-groups',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/groups.vue'),
    },
    // Dedicated help/profile route for the built-in help account so left/right bars
    // and layout match other pages. This must be declared before the generic
    // /user/:username route to take precedence.
    {
      path: '/user/dougjones-help',
      name: 'dougjones-help',
      meta: { layout: 'default', auth: false },
      component: () => import('../views/dougjones-help.vue'),
    },
    {
      path: '/group/:groupId/channel/:id',
      name: 'channel',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/channel.vue'),
    },
    {
      path: '/followers/:id',
      name: 'followers',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/followers.vue'),
    },
    {
      path: '/following/:id',
      name: 'following',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/followers.vue'),
    },
    {
      path: '/referrals',
      name: 'referrals',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/referrals.vue'),
    },
    {
      path: '/notifications',
      name: 'notifications',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/notifications.vue'),
    },
    {
      path: '/messages',
      name: 'messages',
      meta: { layout: 'default', auth: true, hideRightbar: true },
      component: () => import('../views/messages.vue'),
    },
    {
      path: '/twoFactorAuth',
      name: 'twoFactorAuth',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/twoFactorAuth.vue'),
    },
    {
      path: '/sign-up',
      name: 'signup',
      meta: { layout: 'guest', auth: false},
      component: () => import('../views/onboarding/SignUpForm.vue')
    },
    // {
    //   path: '/onboarding/create-username',
    //   name: 'create-username',
    //   meta: { layout: 'guest', auth: false },
    //   component: () => import('../views/onboarding/CreateUsername.vue'),
    // },
    // {
    //   path: '/onboarding/create-account',
    //   name: 'create-account',
    //   meta: { layout: 'guest', auth: false },
    //   component: () => import('../views/onboarding/CreateAccount.vue'),
    // },
    {
      path: '/onboarding/verification-code',
      name: 'verification-code',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/OtpCode.vue'),
    },
    {
      path: '/onboarding/create-password',
      name: 'create-password',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/PasswordSetup.vue'),
    },
    {
      path: '/onboarding/add-avatar',
      name: 'add-avatar',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/AddProfilePhoto.vue'),
    },
    // {
    //   path: '/onboarding/login-method',
    //   name: 'login-method',
    //   meta: { layout: 'guest', auth: false },
    //   component: () => import('../views/onboarding/LoginMethod.vue'),
    // },
    // SIWF (Sign in with Farcaster) routes — disabled: VITE_SIWF_LOGIN_URL is
    // empty in every .env file and no component links to siwfLoginUrl anymore,
    // so there's no UI entry point left to reach these.
    // {
    //   path: '/siwf/callback',
    //   name: 'siwf-callback',
    //   meta: { layout: 'guest', auth: false },
    //   component: () => import('../views/siwf-callback.vue'),
    // },
    // {
    //   path: '/siwf-login',
    //   name: 'siwf-login',
    //   meta: { layout: 'guest', auth: false },
    //   component: () => import('../views/siwf-login.vue'),
    // },
    // {
    //   path: '/siwf/register',
    //   name: 'siwf-register',
    //   meta: { layout: 'guest', auth: false },
    //   component: () => import('../views/siwf-register.vue'),
    // },
    // {
    //   // Legacy alias to support old links like /siwf/register?...
    //   path: '/siwf/register',
    //   redirect: { name: 'siwf-register' },
    //   meta: { layout: 'guest', auth: false },
    // },
    {
      path: '/verify-email',
      name: 'verify-email',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/verify-email.vue'),
    },
    {
      // path: '/check-visit-links',
      path: '/l/:id',
      name: 'check-visit-links',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/check-visit-links.vue'),
    },
    {
      path: '/embed-test',
      name: 'embed-test',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/embed-test.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      meta: { layout: 'guest', auth: false },
      component: () => import('../components/Page404/index.vue'),
    },
    // {
    //   path: '/aboutus',
    //   name: 'about',
    //   meta: { layout: 'default', auth: true },
    //   component: () => import('../views/about/aboutus.vue'),
    // },
    // {
    //   path: '/postmanagement',
    //   name: 'postmanagement',
    //   meta: { layout: 'default', auth: true },
    //   component: () => import('../views/management/postmanagement.vue'),
    // },
  ],
});

// needed later, implement it per-request (e.g. on fetch URLs) instead of mutating routes.

let previousRoute = null;
router.beforeEach((to, from, next) => {
  // Start full-page loader on route navigation start
  try { store.commit('setRouteLoading', true); } catch {}
  // Save scroll position when leaving the home page or profile
  if (from.name === 'home' || from.name === 'profile' || from.name === 'username') {
    saveScrollPosition(from.name);
  }

  // Check authentication for routes that require it
  const isAuthenticated = store.getters.getAuthStatus;

  // Preserve original navigation behavior without added redirects for welcome/home

  // If user is not authenticated and trying to access protected routes, redirect to landing
  if (!isAuthenticated && to.meta.auth) {
    // next('/welcome');
    next('/sign-in');
    return;
  }
  // Signed-in users shouldn't land back on the sign-up page
  if (isAuthenticated && to.name === 'signup') {
    next('/');
    return;
  }
  // Signed-in users shouldn't land back on the sign-in page either
  // (e.g. browser back/forward restoring a pre-login history entry)
  if (isAuthenticated && to.name === 'sign-in') {
    next('/');
    return;
  }
  next();
  previousRoute = from;
});

router.afterEach((to, from) => {
  // Restore scroll position when returning to profile/username pages.
  // Home feed handles its own restoration in index.vue (after posts render).
  if (to.name === 'username' || to.name === 'profile') {
    restoreScrollPosition(to.name, 300);
  }

  // Only send tracking if the path actually changed, and skip the pre-auth
  // sign-in/sign-up/onboarding screens (no session to attribute the page view to yet)
  const preAuthTrackingPaths = [
    '/sign-in',
    '/sign-up',
    '/siwf/callback',
    '/onboarding/verification-code',
    '/onboarding/create-password',
    '/onboarding/add-avatar',
  ];
  if (to.path !== from.path && !preAuthTrackingPaths.includes(to.path)) {
    store.dispatch('sendTrackPage', { url: to.path });
  }

  // End full-page loader on route navigation complete
  try { store.commit('setRouteLoading', false); } catch {}
});

// Ensure loader hides on navigation errors (e.g., dynamic import failures)
// OLD:
// router.onError(() => {
//   try { store.commit('setRouteLoading', false); } catch {}
// });
// FIX: every route component is a lazy chunk with a hashed filename. After a
// redeploy, tabs opened before the deploy still reference the OLD chunk URLs —
// the dynamic import 404s, the navigation aborts here silently, and clicking
// a leftbar menu appears to do nothing until the user hard-refreshes.
// Recover by hard-navigating to the target route so the browser loads the
// fresh index.html + new chunk hashes. Guarded to one auto-reload per minute
// to avoid a reload loop if the network itself is down.
router.onError((error, to) => {
  try { store.commit('setRouteLoading', false); } catch {}
  const message = String(error?.message || '');
  const isStaleChunk =
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk [^ ]+ failed|Loading CSS chunk/i.test(message);
  if (isStaleChunk) {
    let lastReload = 0;
    try { lastReload = Number(sessionStorage.getItem('chunk-reload-ts') || 0); } catch {}
    if (Date.now() - lastReload > 60000) {
      try { sessionStorage.setItem('chunk-reload-ts', String(Date.now())); } catch {}
      window.location.assign(to?.fullPath || window.location.href);
    }
  }
});

export function goBack() {
  const current = router.currentRoute.value;
  if (current.name === 'home') return;

  // Try native back first
  if (window.history.length > 1) {
    router.back();
    return;
  }

  // Fallback to previousRoute if available
  if (previousRoute && previousRoute.name && previousRoute.name !== current.name) {
    router.push({ name: previousRoute.name, params: previousRoute.params, query: previousRoute.query });
    return;
  }

  // Final fallback
  router.push({ name: 'home' });
}

export { previousRoute };
export default router;
